import { BsCheck, BsX, BsArrowRepeat, BsExclamationCircle } from 'react-icons/bs';
import { upperFirst } from 'lodash';

import { Box, BoxProps, styled, themeGet } from '@qwealth/qcore';
import { ContactStatus, ContactType, MemberType, TaskFrequency } from '@qwealth/qdata';
import { ComplianceChecklistActor, ComplianceChecklistStatus } from 'data/models/Compliance';
import { ClientTaxDocumentsOverallStatus, TaxDocumentStatus } from 'data/models/taxDocuments';
import { Tooltip } from 'react-tooltip';

export type TagTitle =
  | ContactType
  | ContactStatus
  | ComplianceChecklistStatus
  | ComplianceChecklistActor
  | 'In Progress'
  | MemberType
  | 'Power of Attorney'
  | 'Authorized Individual'
  | 'Missing Authorized Individual'
  | 'Investment Client'
  | 'Not Investment Client'
  | 'Foreign Primary Address'
  | 'KYC Reminder'
  | 'KYC Warning'
  | 'Household'
  | 'Individual'
  | 'Corporate'
  | 'Compliance Approved'
  | 'Not Compliance Approved'
  | 'Re-Review Pending'
  | 'Wealthbox'
  | 'Significant Change'
  | 'Meaningful Interaction'
  | 'Portfolio Review'
  | 'Overdue'
  | 'Overdue Task'
  | 'Blocked'
  | 'Advisor View'
  | 'Client View'
  | 'Re-Review Pending'
  | 'Recurring'
  | 'Task Tag'
  | TaskFrequency
  | TaxDocumentStatus
  | ClientTaxDocumentsOverallStatus
  | 'Canchek Validation Issue'
  | 'True PEP'
  | 'Canchek Override Applied'
  | 'Completed'
  | 'Invitation Sent'
  | 'Signed Up'
  | 'Not Sent'
  | 'Data Error'
  | 'Pending Review'
  | 'Current'
  | 'Nearing Expiry'
  | 'Expired'
  | 'Unresponsive';


type TagColor = 'Green' | 'Blue' | 'Red' | 'Yellow' | 'Gray' | 'Light Gray';

type TagIcons = 'Check Mark' | 'Warning' | 'X' | 'Recurring' | 'Task Frequency' | 'Overdue';

const tagColorMap: Record<TagTitle, TagColor> = {
  Active: 'Green',
  Inactive: 'Red',
  Deceased: 'Red',
  'Former Client': 'Red',
  'Investment Client': 'Blue',
  'Not Investment Client': 'Gray',
  'Foreign Primary Address': 'Red',
  'KYC Reminder': 'Gray',
  'KYC Warning': 'Red',
  'Primary Client': 'Light Gray',
  'Secondary Client': 'Light Gray',
  'Additional Client': 'Light Gray',
  Lead: 'Blue',
  'Household Member': 'Blue',
  'Related Contact': 'Blue',
  'Power of Attorney': 'Blue',
  Beneficiary: 'Blue',
  'Non-Client Spouse': 'Blue',
  'Authorized Individual': 'Blue',
  'Missing Authorized Individual': 'Red',
  Dependant: 'Blue',
  Household: 'Blue',
  Individual: 'Blue',
  Corporate: 'Blue',
  Complete: 'Green',
  'Not Started': 'Gray',
  'Action Required': 'Red',
  'Advisor Action Required': 'Red',
  'Re-review Required': 'Blue',
  'Compliance Approved': 'Blue',
  'Not Compliance Approved': 'Red',
  'Advisor View': 'Blue',
  'Client View': 'Blue',
  'Re-Review Pending': 'Blue',
  Wealthbox: 'Blue',
  'Significant Change': 'Blue',
  'Meaningful Interaction': 'Blue',
  'Portfolio Review': 'Blue',
  'In Progress': 'Blue',
  Overdue: 'Red',
  'Overdue Task': 'Red',
  Blocked: 'Yellow',
  Recurring: 'Blue',
  'Task Tag': 'Gray',
  Daily: 'Blue',
  Weekly: 'Blue',
  'Bi-Weekly': 'Blue',
  Monthly: 'Blue',
  'Bi-Monthly': 'Blue',
  Quarterly: 'Blue',
  'Semi-Annually': 'Blue',
  Annually: 'Blue',
  'Admin / Advisor': 'Blue',
  PM: 'Blue',
  Compliance: 'Blue',
  expected: 'Red',
  received: 'Blue',
  delivered: 'Green',
  uploaded: 'Blue',
  'Awaiting Documents': 'Red',
  'Pending Delivery': 'Blue',
  'Ready To File': 'Green',
  Filed: 'Green',
  'Canchek Validation Issue': 'Red',
  'True PEP': 'Blue',
  'Canchek Override Applied': 'Blue',
  Completed: 'Green',
  'Invitation Sent': 'Blue',
  'Signed Up': 'Green',
  'Not Sent': 'Light Gray',
  'Data Error': 'Red',
  'Pending Review': 'Blue',
  Current: 'Green',
  'Nearing Expiry': 'Yellow',
  Expired: 'Red',
  Unresponsive: 'Red'
};

export type TooltipDetails = {
  id: string;
  message: JSX.Element;
};

interface InfoTagProps {
  tagTitle: TagTitle;
  icon?: TagIcons;
  numberToDisplay?: number;
  toolTipDetails?: TooltipDetails;
  overrideColor?: TagColor;
  removePadding?: boolean;
  taskTagTitle?: string;
}

type CombinedProps = InfoTagProps & BoxProps;

const InfoTag: React.FC<CombinedProps> = ({
  tagTitle,
  fontSize,
  fontWeight,
  icon,
  numberToDisplay,
  toolTipDetails,
  overrideColor,
  removePadding,
  taskTagTitle,
}) => {
  const status = overrideColor ? overrideColor : tagColorMap[tagTitle];

  const getTagTitle = (): string => {
    switch (tagTitle) {
      case 'KYC Reminder':
        return `KYC: ${numberToDisplay?.toString()} Days Remaining`;
      case 'KYC Warning':
        // Ensure numberToDisplay is defined and convert to positive: (numberToDisplay ?? 0) * -1)
        return `KYC: ${((numberToDisplay ?? 0) * -1).toString()} Days Overdue`;
      case 'Task Tag':
        return `${taskTagTitle ?? ''}`;
      default:
        return upperFirst(tagTitle);
    }
  };

  const getPaddingForTagTitle = () => {
    if (['KYC Reminder', 'KYC Warning', 'Primary Client', 'Secondary Client', 'Additional Client'].includes(tagTitle) || removePadding) {
      return themeGet('space.none');
    }
    return themeGet('space.small');
  };

  const getColorForStatus = () => {
    switch (status) {
      case 'Green':
        return themeGet('colors.green');
      case 'Blue':
        return themeGet('colors.blue');
      case 'Red':
        return themeGet('colors.red');
      case 'Yellow':
        return themeGet('colors.yellow');
      case 'Gray':
        return themeGet('colors.gray.1');
      case 'Light Gray':
        return themeGet('colors.gray.0');
    }
  };

  const getBackgroundColorForStatus = () => {
    switch (status) {
      case 'Green':
        return '#F9FFF4';
      case 'Blue':
        return themeGet('colors.blueTransparent');
      case 'Red':
        return themeGet('colors.redTransparent');
      case 'Yellow':
        return themeGet('colors.yellowTransparent');
      case 'Gray':
        return themeGet('colors.blueTransparent');
      case 'Light Gray':
        return themeGet('colors.gray.4');
    }
  };

  const getFontSizeForStatus = () => {
    if (fontSize) {
      return themeGet('fontSizes.' + fontSize);
    }

    return themeGet('fontSizes.default');
  };

  const InfoTag = styled(Box)`
    background-color: ${getBackgroundColorForStatus()};
    color: ${getColorForStatus()};
    border-radius: ${themeGet('radii.default')};
    padding: ${getPaddingForTagTitle()};
    padding-left: ${themeGet('space.default')};
    padding-right: ${themeGet('space.default')};
    font-size: ${getFontSizeForStatus()};
    width: fit-content;
    max-height: fit-content;
    display: flex;
  `;

  return (
    <Box>
      <InfoTag
        fontWeight={fontWeight ?? 'bold'}
        fontSize={fontSize ?? 'default'}
        gap="small"
        alignItems="center"
        data-tooltip-id={toolTipDetails?.id}
        data-tip
      >
        {getTagTitle()}
        {icon === 'Check Mark' && tagTitle !== 'Not Investment Client' && <BsCheck size="20px" />}
        {icon === 'X' && <BsX size="20px" />}
        {icon === 'Recurring' && <BsArrowRepeat size="24px" />}
        {icon === 'Task Frequency' && <BsArrowRepeat size="18px" />}
        {icon === 'Overdue' && <BsExclamationCircle size="16px" />}
      </InfoTag>
      {toolTipDetails !== undefined && (
        <Tooltip id={toolTipDetails.id}>
          <Box fontSize="small">{toolTipDetails.message}</Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default InfoTag;
